import { Stack, Typography } from "@mui/material";
import { Controller } from "react-hook-form";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import React, { useState } from "react";
import Selection from "components/Input/Selection/Selection";
import Datepicker from "components/Input/Datepicker/Datepicker";
import Checkbox from "components/Input/Checkbox/Checkbox";
import { useTranslation } from "react-i18next";

import LocationCascader from "./LocationCascader";
import ImageUploader from "./ImageUploader";
import { CustomComponentCommonProps, rules } from "../utils";
const MerchantInfoSession = ({
  control,
  setValue,
  setError,
  clearErrors,
  reset,
  watch,
  isEdit,
  isLoading,
  merchantId,
}) => {
  const alignStoreInfo = (isSameAsCompanyInfo) => {
    const formValues = watch();
    console.log("formValues", formValues);
    const baseValues = {
      storeName: "",
      storeNameSecondaryLang: "",
      address1: "",
      storeAddressSecondaryLang: "",
      country: "",
    };

    if (isSameAsCompanyInfo) {
      reset({
        ...formValues,
        storeName: formValues.name,
        storeNameSecondaryLang: formValues.nameSecondaryLang,
        address1: formValues.ownerAddress1,
        storeAddressSecondaryLang: formValues.ownerAddress2,
        country: formValues.ownerCountry,
      });
    } else {
      reset({
        ...formValues,
        ...baseValues,
      });
    }
  };
  const { t } = useTranslation();
  const PAGE_LOCALE_KEY = "page.merchants.";
  const FORM_LABEL_KEY =
    PAGE_LOCALE_KEY + "merchantDetail.info.merchantInfo.fields";
  const [previewUrl, setPreviewUrl] = useState("");

  return (
    <Stack direction={"column"} rowGap={2} width={"80%"}>
      <Stack direction={"row"} alignItems={"center"} gap={2}>
        <Typography variant="h3">
          {t(`${PAGE_LOCALE_KEY}merchantDetail.info.merchantInfo.title`)}
        </Typography>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <Checkbox
            disabled={!isEdit}
            oncheckFn={(e) => {
              alignStoreInfo(e?.target?.checked);
            }}
          />
          <Typography> Same as company information</Typography>
        </Stack>
      </Stack>

      <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
        <Controller
          name="storeName"
          control={control}
          defaultValue=""
          rules={rules({
            required: true,
            maxLength: 22,
            validationTags: [
              "noLeadingWhitespace",
              "noQuotesOrBackticks",
              "noChineseCharacters",
              "noFullwidthCharacters",
              "noSpecialSpaces",
            ],
          })}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <OutlinedText
              mandatory
              errorMessage={error?.message || ""} // Show actual error message
              onchangeFn={onChange}
              divStyle={{ flexGrow: 1, flexBasis: "600px" }}
              sxParams={{
                textError: !!error,
              }}
              {...CustomComponentCommonProps(
                t(`${FORM_LABEL_KEY}.storeName`),
                value,
                "OutlinedText",
                true,
                isEdit,
                isLoading,
              )}
            />
          )}
        />
        <Controller
          name="storeNameSecondaryLang"
          control={control}
          defaultValue=""
          rules={rules({
            maxLength: 255,
            validationTags: ["noLeadingWhitespace", "noQuotesOrBackticks"],
          })}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <OutlinedText
              errorMessage={error?.message || ""}
              onchangeFn={onChange}
              divStyle={{ flexGrow: 1, flexBasis: "600px" }}
              {...CustomComponentCommonProps(
                t(`${FORM_LABEL_KEY}.storeNameSecondaryLang`),
                value,
                "OutlinedText",
                false,
                isEdit,
                isLoading,
              )}
              sxParams={{
                textError: !!error,
              }}
            />
          )}
        />
      </Stack>
      <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
        <Controller
          name="address1"
          control={control}
          defaultValue=""
          rules={rules({
            required: true,
            maxLength: 255,
            validationTags: [
              "noLeadingWhitespace",
              "onlyAlphabetNumAndSpaces",
              "noFullwidthCharacters",
              "noSpecialSpaces",
            ],
          })}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <OutlinedText
              mandatory
              errorMessage={error?.message || ""}
              onchangeFn={onChange}
              divStyle={{ flexGrow: 1, flexBasis: "600px" }}
              sxParams={{
                textError: !!error,
              }}
              {...CustomComponentCommonProps(
                t(`${FORM_LABEL_KEY}.address1`),
                value,
                "OutlinedText",
                true,
                isEdit,
                isLoading,
              )}
            />
          )}
        />
        <Controller
          name="storeAddressSecondaryLang"
          control={control}
          defaultValue=""
          rules={rules({
            maxLength: 255,
            validationTags: [
              "noLeadingWhitespace",
              "onlyAlphabetNumChineseAndSpaces",
            ],
          })}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <OutlinedText
              errorMessage={error?.message || ""}
              onchangeFn={onChange}
              divStyle={{ flexGrow: 1, flexBasis: "600px" }}
              {...CustomComponentCommonProps(
                t(`${FORM_LABEL_KEY}.storeAddressSecondaryLang`),
                value,
                "OutlinedText",
                false,
                isEdit,
                isLoading,
              )}
              sxParams={{
                textError: !!error,
              }}
            />
          )}
        />
        <Controller
          name="country"
          control={control}
          defaultValue=""
          rules={rules({
            required: true,
          })}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Selection
              errorMessage={error?.message || ""}
              onchangeFn={onChange}
              divStyle={{ flexGrow: 1, flexBasis: "600px" }}
              options={[
                {
                  key: "hk",
                  title: "Hong Kong",
                  value: "HK",
                },
                {
                  key: "sg",
                  title: "Singapore",
                  value: "SG",
                },
              ]}
              {...CustomComponentCommonProps(
                t(`${FORM_LABEL_KEY}.country`),
                value,
                "Selection",
                true,
                isEdit,
                isLoading,
              )}
              sxParams={{
                selectionError: !!error,
              }}
            />
          )}
        />
      </Stack>
      <LocationCascader
        control={control}
        setValue={setValue}
        isEdit={isEdit}
        isLoading={isLoading}
      />
      <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
        <Controller
          name="reportEmail"
          control={control}
          defaultValue=""
          rules={rules({
            validationTags: ["validEmail"],
          })}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <OutlinedText
              errorMessage={error?.message || ""}
              onchangeFn={onChange}
              divStyle={{ flexGrow: 1, flexBasis: "400px" }}
              sxParams={{
                textError: !!error,
              }}
              {...CustomComponentCommonProps(
                t(`${FORM_LABEL_KEY}.reportEmail`),
                value,
                "OutlinedText",
                false,
                isEdit,
                isLoading,
              )}
            />
          )}
        />
        <Controller
          name="businessRegistration"
          control={control}
          defaultValue=""
          rules={rules({
            required: true,
            validationTags: ["validBusinessRegNo"],
          })}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <OutlinedText
              errorMessage={error?.message || ""}
              onchangeFn={onChange}
              divStyle={{ flexGrow: 1, flexBasis: "600px" }}
              {...CustomComponentCommonProps(
                t(`${FORM_LABEL_KEY}.businessRegistration`),
                value,
                "OutlinedText",
                true,
                isEdit,
                isLoading,
              )}
              sxParams={{
                textError: !!error,
              }}
            />
          )}
        />
        <Controller
          name="businessRegistrationExpiryDate"
          control={control}
          defaultValue=""
          rules={rules({ required: true, validationTags: ["validDate"] })}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Datepicker
              divStyle={{ flexGrow: 1, flexBasis: "600px" }}
              errorMessage={error?.message || ""}
              type="start"
              onchangeFn={onChange}
              sxParams={{
                datepickerError: !!error,
              }}
              {...CustomComponentCommonProps(
                t(`${FORM_LABEL_KEY}.businessRegistrationExpiryDate`),
                value,
                "Datepicker",
                true,
                isEdit,
                isLoading,
              )}
            />
          )}
        />
      </Stack>
      <ImageUploader
        name={"logo"}
        control={control}
        setError={setError}
        clearErrors={clearErrors}
        isEdit={isEdit}
        isLoading={isLoading}
        description={`(Maximum image size: 2MB; Format: jpg and png only; Resolution:
          250x250px - 1024x1024px)`}
        setValue={setValue}
        previewUrl={previewUrl}
        setPreviewUrl={setPreviewUrl}
        mandatory={true}
        merchantId={merchantId}
      />
    </Stack>
  );
};
export default React.memo(MerchantInfoSession);
